import { camelCaseToWords } from '@apps-common/helpers/camel-case-to-words'
import { capitalizeEveryWord } from '@apps-common/helpers/capitalizeEachWord'
import { ISelectOption } from '@apps-common/types/form'
import { IReportCompanyUnitGroupModel, IReportCompanyUnitModel } from '@apps-common/types/management/reporting'
import { upperFirst } from 'lodash'
import moment from 'moment'
import * as XLSX from 'xlsx-js-style'

export const getAvailablePropertiesForUnitsReport = (): ISelectOption[] => {
  var item: IReportCompanyUnitModel = {
    unitId: '',
    productName: '',
    monitoringCentre: '',
    bureau: '',
    plan: '',
    firmware: '',
    accountNo: '',
    simId: '',
    apn: '',
    simType: '',
    mobileNo: '',
    simNo: '',
    ip: '',
    dateEdited: '',
    dateLoaded: '',
    dateCancelled: '',
    editedBy: ''
  }

  const properties: ISelectOption[] = []

  Object.keys(item).forEach((key) => {
    const value = item[key as any as keyof IReportCompanyUnitModel]
    properties.push({
      label: capitalizeEveryWord(camelCaseToWords(key)),
      value: key
    })
  })

  return properties
}

export const createAndDownloadExcel = (
  data: IReportCompanyUnitGroupModel[],
  name: string,
  startDate: string,
  includedProperties: ISelectOption[]
) => {
  // create workbook
  const wb = XLSX.utils.book_new()

  // add worksheets for each status
  for (let i = 0; i < data.length; i++) {
    const group = data[i]
    let hasNoData = false
    let wsData: any[] = []

    if (group.data.length === 0) {
      const item = {
        unitId: '',
        productName: '',
        monitoringCentre: '',
        bureau: '',
        plan: '',
        firmware: '',
        accountNo: '',
        simId: '',
        apn: '',
        simType: '',
        mobileNo: '',
        simNo: '',
        ip: '',
        dateEdited: '',
        dateLoaded: '',
        dateCancelled: '',
        editedBy: ''
      }

      if (group.status !== 'cancelled') {
        delete item['dateCancelled' as keyof typeof item]
      }

      hasNoData = true
      wsData.push(item)
    } else {
      wsData = group.data.map((unit) => {
        const item = {
          unitId: unit.unitId,
          productName: unit.productName,
          monitoringCentre: unit.monitoringCentre,
          bureau: unit.bureau,
          plan: unit.plan,
          firmware: unit.firmware,
          accountNo: unit.accountNo,
          simId: unit.simId,
          apn: unit.apn,
          simType: unit.simType,
          mobileNo: unit.mobileNo,
          simNo: unit.simNo,
          ip: unit.ip,
          dateEdited: new Date(unit.dateEdited),
          dateLoaded: new Date(unit.dateLoaded),
          dateCancelled: new Date(unit.dateCancelled || ''),
          editedBy: unit.editedBy
        }

        if (group.status !== 'cancelled') {
          delete item['dateCancelled' as keyof typeof item]
        }

        return item
      })
    }

    wsData = wsData.map((unit) => {
      const item: any = {}

      for (let j = 0; j < includedProperties.length; j++) {
        const prop = includedProperties[j]
        const label = prop.label
        const key = prop.value.toString()
        if (key in unit) {
          item[label] = unit[key as keyof IReportCompanyUnitModel]
        }
      }

      if (group.status !== 'cancelled') {
        delete item['Date Cancelled']
      }

      return item
    })

    const ws = XLSX.utils.json_to_sheet(wsData)

    // add styling to sheet

    if (!hasNoData) {
      const cellBorderStyle = {
        top: { style: 'thin', color: { rgb: '000000' } }, // Black color
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } }
      }

      const bodyRange = XLSX.utils.decode_range(ws['!ref']!)

      for (let R = bodyRange.s.r; R <= bodyRange.e.r; ++R) {
        for (let C = bodyRange.s.c; C <= bodyRange.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
          if (!ws[cellAddress]) continue // Skip if cell is empty or doesn't exist

          // Initialize the style object for the cell if it doesn't exist
          if (!ws[cellAddress].s) {
            ws[cellAddress].s = {}
          }

          // Apply the border style
          ws[cellAddress].s.border = cellBorderStyle
        }
      }
    }

    // add header styling

    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFFFF' } }, // White font color
      fill: {
        type: 'pattern',
        patternType: 'solid', // Must be 'solid' for a single background color
        fgColor: { rgb: 'FF0072C6' } // Blue background color (ARGB hex value)
      },
      alignment: { horizontal: 'left' } // Center alignment
    }

    // 4. Iterate through the header cells and apply the style
    const headerRange = XLSX.utils.decode_range(ws['!ref']!)
    const headerRowIndex = headerRange.s.r // Typically row 0 for headers

    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ c: col, r: headerRowIndex })
      if (ws[cellRef]) {
        ws[cellRef].s = headerStyle
      }
    }

    // add min widths
    const minWidth = 20 // Set your desired minimum width here

    // 3. Determine the number of columns in your worksheet
    // You can deduce the range of used cells
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')
    const numCols = range.e.c + 1 // range.e.c is 0-indexed

    // 4. Create an array of column width objects
    // This applies the same width to every column
    const columnWidths = Array.from({ length: numCols }, () => ({ wch: minWidth }))

    // 5. Assign the widths to the worksheet's '!cols' property
    ws['!cols'] = columnWidths

    XLSX.utils.book_append_sheet(wb, ws, upperFirst(group.status))
  }

  // write and download workbook
  XLSX.writeFile(wb, `${name.toLowerCase().replaceAll(' ', '_')}.${moment(startDate).format('DD.MM.YY')}.xlsx`)
}
